import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import MuiIconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { StatusChip, Banner, Disclosure as MuiDisclosure } from "../mui";
import { Ring } from "../loading";
import { useApp } from "../../design/AppContext";
import { apiGet } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { MdClose, MdDeleteOutline, MdStopCircle, MdVisibility } from "react-icons/md";

/**
 * 專案原碼檢測（/CodeScan）與專案弱點掃描（/VulnScan）共用的零件。
 *
 * 兩頁的骨架一模一樣：選專案 → 維護清單 → 排入掃描 → 歷史（伺服器分頁＋輪詢）→ 報告抽屜。
 * 會踩雷的地方（輪詢重疊、切專案時舊請求回來蓋掉新資料、抽屜定位、深連結）集中寫在這裡，
 * 兩頁各自只管欄位與版面，之後修一個坑不必改兩次。
 *
 * 全部直接走 utils/api.js，不走 saga：saga 失敗會 window.alert，
 * 5 秒一次的輪詢只要網路抖一下就會連跳好幾個對話框。
 *
 * 顏色與字級都已經壓到新設計的 token（設計規則 6）：狀態色不再寫死 hex，
 * font-size 也一律走 --t-*，這樣切到大字模式時這三頁才會跟著放大 ——
 * 先前只搬了顏色半邊，同一個 style 字串裡的 13px 被留著，大字模式就在這裡失效。
 * ZAP 的風險分高／中／低／資訊四階，新 token 只有三組對得上：
 * 高 → --attention、中與低都 → --warn-ink、資訊 → --closed。
 * 中與低同色是**有意的降階**，不是漏改 —— 硬擠出第四個顏色等於自己發明一個 token，
 * 下次重搬設計稿就會對不起來；而「中風險」「低風險」這幾個字本來就寫在每一列上，
 * 少一階顏色不會讓人看錯是哪一階。
 */

/* ===================== 狀態常數 ===================== */

/*
 * kind 是 StatusChip 的語意色（膠囊用）；c 是文字色、bg 是底色，都走 token ——
 * 頁面上數字標色（KPI、風險計數）仍直接拿 c 來用，所以兩組都留著。
 * 深淺色兩套佈景由 tokens.css 自己換值，用到的人不必知道現在是深是淺。
 */
const GREY = "var(--closed)";
const GREY_BG = "var(--closed-soft)";
const BLUE = "var(--primary)";
const BLUE_BG = "var(--primary-soft)";
const GREEN = "var(--done)";
const GREEN_BG = "var(--done-soft)";
const AMBER = "var(--warn-ink)";
const AMBER_BG = "var(--warn-soft)";
const RED = "var(--attention)";
const RED_BG = "var(--attention-soft)";
/** 低風險原本是黃褐色（#b7791f），併進 --warn-ink 與「中」同色 —— 見檔頭的降階說明 */
const OCHRE = AMBER;
const OCHRE_BG = AMBER_BG;

/** 批次狀態（code_scan_run / vuln_scan_run.status） */
export const RUN_STATUS = {
  0: { label: "排隊中", kind: "closed", c: GREY, bg: GREY_BG },
  1: { label: "掃描中", kind: "progress", c: BLUE, bg: BLUE_BG },
  2: { label: "完成", kind: "done", c: GREEN, bg: GREEN_BG },
  3: { label: "失敗", kind: "attention", c: RED, bg: RED_BG },
  4: { label: "已取消", kind: "closed", c: GREY, bg: GREY_BG },
  5: { label: "部分失敗", kind: "warn", c: AMBER, bg: AMBER_BG },
};

/** 單一 repo／網址的結果狀態（*_scan_result.status）；4 是批次被取消時沒輪到的項目 */
export const ITEM_STATUS = {
  0: { label: "排隊中", kind: "closed", c: GREY, bg: GREY_BG },
  1: { label: "執行中", kind: "progress", c: BLUE, bg: BLUE_BG },
  2: { label: "成功", kind: "done", c: GREEN, bg: GREEN_BG },
  3: { label: "失敗", kind: "attention", c: RED, bg: RED_BG },
  4: { label: "已略過", kind: "closed", c: GREY, bg: GREY_BG },
};

/** SonarQube 品質門檻 */
export const GATE = {
  OK: { label: "通過", kind: "done", c: GREEN, bg: GREEN_BG },
  WARN: { label: "警告", kind: "warn", c: AMBER, bg: AMBER_BG },
  ERROR: { label: "未通過", kind: "attention", c: RED, bg: RED_BG },
  NONE: { label: "無", kind: "closed", c: GREY, bg: GREY_BG },
};

/** ZAP 風險等級（alert.risk：0=Informational 1=Low 2=Medium 3=High）；中與低同色，見檔頭 */
export const RISK = {
  3: { label: "高", kind: "attention", c: RED, bg: RED_BG },
  2: { label: "中", kind: "warn", c: AMBER, bg: AMBER_BG },
  1: { label: "低", kind: "warn", c: OCHRE, bg: OCHRE_BG },
  0: { label: "資訊", kind: "closed", c: GREY, bg: GREY_BG },
};

/** 排隊中或執行中 —— 還會變的批次，才需要輪詢、才可以取消、不可以刪除 */
export const isActiveStatus = (status) => Number(status) === 0 || Number(status) === 1;

/* ===================== 格式 ===================== */

/** 秒數 → `1 分 23 秒`／`45 秒`／`1 小時 2 分`；沒開始（null）顯示「—」 */
export function fmtDuration(seconds) {
  if (seconds === null || seconds === undefined || seconds === "") return "—";
  const n = Math.max(0, Math.round(Number(seconds)));
  if (!Number.isFinite(n)) return "—";
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  if (h > 0) return m > 0 ? `${h} 小時 ${m} 分` : `${h} 小時`;
  if (m > 0) return s > 0 ? `${m} 分 ${s} 秒` : `${m} 分`;
  return `${s} 秒`;
}

/**
 * 計數欄位：null 代表「沒有數據」（失敗、還沒跑完），跟 0（跑完了、真的沒有）意思完全不同，
 * 所以 null 一律顯示「—」而不是 0。
 */
export const fmtCount = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString());

/**
 * 只放行 http(s) 網址。dashboard_url、ZAP 的參考連結都是 worker 或外部工具寫進來的字串，
 * 直接塞進 href 的話，一個 `javascript:` 開頭的值就能在使用者點下去時執行任意程式。
 */
export const safeHttpUrl = (u) => {
  const s = String(u || "").trim();
  return /^https?:\/\//i.test(s) ? s : "";
};

/**
 * ZAP 的說明與建議常夾著 <p> 標籤。這裡只轉成純文字顯示，
 * 絕不用 dangerouslySetInnerHTML —— 內容來自被掃描的網站與外部工具，不能當 HTML 信任。
 *
 * 放在共用的 kit 裡而不是留在 VulnScan/Index.js：新的「檢測」合併頁也要在明細抽屜裡
 * 顯示同一批說明，兩邊各留一份遲早會改到其中一份。
 */
export const stripHtml = (s) =>
  String(s || "")
    .replace(/<\/p>\s*<p>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .trim();

/**
 * 清單 textarea → [{ line, url, rest }]。
 * 一行一筆、第一個空白前是網址、之後整段是附加欄位（分支或名稱）；空行與 # 開頭的註解略過。
 * 帶行號是為了前端檢查時能講「第幾行」有問題，不然使用者要自己一行一行找。
 */
export function parseListLines(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((raw, i) => ({ line: i + 1, raw: raw.trim() }))
    .filter((x) => x.raw && !x.raw.startsWith("#"))
    .map(({ line, raw }) => {
      const m = raw.match(/^(\S+)(?:\s+(.*))?$/);
      return { line, url: m[1], rest: (m[2] || "").trim() };
    });
}

/* ===================== 膠囊與按鈕 ===================== */

/** 膠囊左邊的小轉圈（排隊中、掃描中 —— 表示還在跑）；顏色跟著膠囊文字色 */
const Dot = () => <Ring size={0.9} aria-hidden="true" />;

/**
 * 以 {label,kind,c,bg} 對照表畫一顆膠囊；label 可覆寫（例如「掃描中… 1 分 3 秒」）。
 * 有 kind 就交給 StatusChip 決定顏色；呼叫端自己組、只帶 c／bg 的舊外形，就用 c／bg 當顏色。
 */
export function MetaPill({ meta, label, dot, title }) {
  const m = meta || { label: "—", kind: "closed" };
  return (
    <StatusChip
      kind={m.kind || "closed"}
      size="small"
      mark={dot ? <Dot /> : null}
      title={title || undefined}
      sx={m.kind ? { fontWeight: 600 } : { fontWeight: 600, bgcolor: m.bg, color: m.c }}
    >
      {label || m.label}
    </StatusChip>
  );
}

MetaPill.propTypes = {
  meta: PropTypes.shape({ label: PropTypes.string, kind: PropTypes.string, c: PropTypes.string, bg: PropTypes.string }),
  label: PropTypes.string,
  dot: PropTypes.bool,
  title: PropTypes.string,
};

MetaPill.defaultProps = { meta: null, label: "", dot: false, title: "" };

/**
 * 批次狀態膠囊。執行中直接帶已執行時間 ——
 * 使用者盯著這一格等結果時，最想知道的是「還在動嗎、跑多久了」。
 * 已要求取消但 worker 還沒停下來的，顯示「取消中…」，免得以為按了沒反應。
 */
export function RunStatusPill({ status, durationSeconds, cancelRequested }) {
  const s = Number(status);
  const meta = RUN_STATUS[s];
  if (s === 1) {
    const elapsed = durationSeconds == null ? "" : ` ${fmtDuration(durationSeconds)}`;
    return <MetaPill meta={meta} dot label={cancelRequested ? `取消中…${elapsed}` : `掃描中…${elapsed}`} />;
  }
  return <MetaPill meta={meta} dot={s === 0} />;
}

RunStatusPill.propTypes = {
  status: PropTypes.number,
  durationSeconds: PropTypes.number,
  cancelRequested: PropTypes.bool,
};

RunStatusPill.defaultProps = { status: 0, durationSeconds: null, cancelRequested: false };

/** 品質門檻膠囊；還沒有門檻結果（null）時不畫膠囊，只給一條淡色槓 */
export function GatePill({ gate }) {
  const key = String(gate || "").toUpperCase();
  if (!GATE[key])
    return (
      <Typography component="span" color="text.disabled">
        —
      </Typography>
    );
  return <MetaPill meta={GATE[key]} title={`品質門檻：${GATE[key].label}`} />;
}

GatePill.propTypes = { gate: PropTypes.string };
GatePill.defaultProps = { gate: "" };

const ICON_PATHS = {
  view: <MdVisibility />,
  cancel: <MdStopCircle />,
  delete: <MdDeleteOutline />,
};

const ICON_HOVER = {
  view: "primary.main",
  cancel: "warning.main",
  delete: "error.main",
};

/**
 * 表格列尾的圖示按鈕（檢視報告／取消／刪除紀錄）。
 *
 * 名稱沿用 IconButton（兩頁都 import 這個名字），內部是 MUI 的 IconButton＋Tooltip。
 * 仍然只有圖示（文字在 aria-label 與 Tooltip 裡）；要補可見文字得連同 CodeScan／VulnScan
 * 兩頁的歷史表格一起改欄寬，不在這一輪。
 */
export function IconButton({ icon, title, disabled, onClick }) {
  const btn = (
    <MuiIconButton
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      sx={{ color: "text.secondary", "&:hover": { color: ICON_HOVER[icon] } }}
    >
      {ICON_PATHS[icon]}
    </MuiIconButton>
  );
  return (
    <Tooltip title={title}>
      {/* disabled 的按鈕不發滑鼠事件，Tooltip 要包一層 span 才收得到 hover */}
      {disabled ? <span>{btn}</span> : btn}
    </Tooltip>
  );
}

IconButton.propTypes = {
  icon: PropTypes.oneOf(["view", "cancel", "delete"]),
  title: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

IconButton.defaultProps = { icon: "view", title: "", disabled: false, onClick: () => {} };

/* ===================== KPI 卡片列 ===================== */

/** 版面沿用 Dashboard/Overview 的 KPI 卡，縮成適合放在卡片與抽屜裡的尺寸 */
export function KpiRow({ items, min }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: 1.5 }}>
      {items.map((k) => (
        <Card key={k.key} title={k.hint || undefined} sx={{ px: 2, py: 1.75, minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ fontWeight: 600 }}
          >
            {k.label}
          </Typography>
          <Typography
            component="div"
            sx={{
              fontSize: "var(--t-title)",
              lineHeight: "var(--t-title-lh)",
              fontWeight: 900,
              letterSpacing: "-.4px",
              mt: 1,
              fontVariantNumeric: "tabular-nums",
              color: k.color || "text.primary",
            }}
          >
            {k.value}
          </Typography>
        </Card>
      ))}
    </Box>
  );
}

KpiRow.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      value: PropTypes.node,
      color: PropTypes.string,
      hint: PropTypes.string,
    })
  ),
  /** 單張卡最小寬度（px）；抽屜比頁面窄，傳小一點才排得下一列 */
  min: PropTypes.number,
};

KpiRow.defaultProps = { items: [], min: 150 };

/* ===================== 報告抽屜外殼 ===================== */

/**
 * 右側報告抽屜（MUI Drawer）。portal、ESC 關閉、鎖背景捲動都由 Drawer 處理。
 * 寬度用百分比上限不用 vw：vw 不扣掉捲軸寬度，會比可視區還寬。
 * 報告比工項詳情多一欄數字，所以寬度放到 760px。
 */
export function ScanDrawer({ open, title, subtitle, badges, actions, onClose, children }) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => onClose()}
      slotProps={{ paper: { sx: { width: 760, maxWidth: "96%", display: "flex", flexDirection: "column" } } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          px: 2.5,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 700 }}>
                {subtitle}
              </Typography>
            )}
            {badges}
          </Box>
          <Typography variant="h3" component="h2" sx={{ mt: 1, letterSpacing: "-.2px" }}>
            {title}
          </Typography>
        </Box>
        {actions}
        {/* 關閉鈕要有文字、命中區要 ≥ 44px（硬規則 3）：純圖示叉叉的 title 只有滑鼠 hover 才看得到 */}
        <Button
          variant="text"
          onClick={() => onClose()}
          title="關閉（Esc）"
          startIcon={<MdClose aria-hidden="true" />}
          sx={{ flexShrink: 0 }}
        >
          關閉
        </Button>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", px: 2.5, pt: 2.25, pb: 3.5 }}>
        {children}
      </Box>
    </Drawer>
  );
}

ScanDrawer.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  /** 標題上方那一列的膠囊（狀態、品質門檻…） */
  badges: PropTypes.node,
  /** 關閉鈕左邊的額外按鈕 */
  actions: PropTypes.node,
  onClose: PropTypes.func,
  children: PropTypes.node,
};

ScanDrawer.defaultProps = {
  open: false,
  title: "",
  subtitle: "",
  badges: null,
  actions: null,
  onClose: () => {},
  children: null,
};

/* ===================== 報告內的小零件 ===================== */

/** 抽屜頂部的時間資訊格（排入／開始／結束／耗時／觸發者） */
export function MetaGrid({ items }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))",
        gap: "1px",
        bgcolor: "divider",
        border: 1,
        borderColor: "divider",
      }}
    >
      {items.map((it) => (
        <Box key={it.label} sx={{ bgcolor: "background.default", px: 1.5, py: 1.25, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {it.label}
          </Typography>
          <Typography
            component="div"
            noWrap
            sx={{ fontWeight: 700, mt: 0.5, fontVariantNumeric: "tabular-nums" }}
            title={typeof it.value === "string" ? it.value : undefined}
          >
            {it.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

MetaGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.node })),
};

MetaGrid.defaultProps = { items: [] };

/** 紅色錯誤訊息區塊（保留換行：編譯失敗的原因常是好幾行） */
export function ErrorNote({ text }) {
  if (!text) return null;
  return (
    <Banner kind="attention" icon={false} sx={{ fontWeight: 600, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {text}
    </Banner>
  );
}

ErrorNote.propTypes = { text: PropTypes.string };
ErrorNote.defaultProps = { text: "" };

/**
 * 可展開區塊 —— 包一層 components/mui 的 Disclosure，介面（label／count）維持原樣。
 * 預設收合：一份報告可能有十幾個 repo，全部攤開會長到找不到東西。
 * 開合狀態接在這層的 state 上，標頭右側的「展開／收合」字樣才知道該寫哪一個。
 */
export function Disclosure({ label, count, children, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <MuiDisclosure
      title={label}
      count={count != null ? `（${count}）` : null}
      open={open}
      onToggle={setOpen}
      summaryExtra={open ? "收合" : "展開"}
    >
      {children}
    </MuiDisclosure>
  );
}

Disclosure.propTypes = {
  label: PropTypes.string,
  count: PropTypes.number,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
};

Disclosure.defaultProps = { label: "", count: null, children: null, defaultOpen: false };

/** 執行紀錄（log_tail）：等寬字、限高可捲動，不然一份建置紀錄就把抽屜撐到底 */
export function LogTail({ text }) {
  if (!text)
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        尚無執行紀錄
      </Typography>
    );
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        maxHeight: 320,
        overflow: "auto",
        px: 1.5,
        py: 1.25,
        bgcolor: "background.default",
        border: 1,
        borderColor: "divider",
        fontFamily: "var(--font-mono)",
        fontSize: "var(--t-code)",
        lineHeight: 1.5,
        color: "text.secondary",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
      }}
    >
      {text}
    </Box>
  );
}

LogTail.propTypes = { text: PropTypes.string };
LogTail.defaultProps = { text: "" };

/* ===================== 頁面區塊（CodeScan／VulnScan 共用的卡片頭、表格樣式） ===================== */

/** 卡片標題列：標題、計數膠囊、說明，右邊放按鈕（中間用 <Box sx={{ flex: 1 }} /> 推開） */
export function SectionHead({ children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        flexWrap: "wrap",
        px: 3,
        py: 2,
        borderBottom: 1,
        borderColor: "divider",
        fontSize: "var(--t-section)",
        lineHeight: "var(--t-section-lh)",
        fontWeight: 700,
      }}
    >
      {children}
    </Box>
  );
}

SectionHead.propTypes = { children: PropTypes.node };
SectionHead.defaultProps = { children: null };

/** 標題列旁的灰色計數膠囊（「已儲存 3 個網址」「共 12 次」） */
export function CountChip({ children }) {
  return <Chip size="small" label={children} sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }} />;
}

CountChip.propTypes = { children: PropTypes.node };
CountChip.defaultProps = { children: null };

/** 標題列旁的次要說明；color 給「有未儲存的變更」這種要提醒的字 */
export function HeadNote({ children, color }) {
  return (
    <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: color || "text.secondary" }}>
      {children}
    </Typography>
  );
}

HeadNote.propTypes = { children: PropTypes.node, color: PropTypes.string };
HeadNote.defaultProps = { children: null, color: "" };

/** 歷史表格：表頭（<TableHead sx>）與內容（<TableBody sx>）的樣式；數字欄用 TableCell 的 align="right" */
export const tableHeadSx = {
  bgcolor: "background.default",
  "& th": { fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap" },
};
export const tableBodySx = {
  "& td": { color: "text.secondary", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
};

/* ===================== Hooks ===================== */

/**
 * 輪詢：active 為 true 才每 ms 毫秒呼叫一次 fn，false 或 unmount 就停。
 *
 * - fn 放在 ref 裡，呼叫端不必 useCallback 也不會每次 render 重設計時器。
 * - 上一次還沒回來就跳過這一輪：後端慢的時候不會越疊越多請求。
 * - 分頁在背景時跳過：沒人在看的頁面不必每 5 秒打一次 API。
 * - 錯誤一律吞掉：輪詢失敗下一輪會自己再試，跳 toast 只會洗版。
 */
export function usePolling(fn, active, ms = 5000) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!active) return undefined;
    let busy = false;
    const t = setInterval(() => {
      if (busy || document.hidden) return;
      busy = true;
      Promise.resolve()
        .then(() => fnRef.current())
        .catch(() => {})
        .finally(() => {
          busy = false;
        });
    }, ms);
    return () => clearInterval(t);
  }, [active, ms]);
}

/**
 * 頁面層狀態：可選專案清單＋網址列上的 ?projectId= 與 ?run=。
 *
 * 選哪個專案、開哪份報告都直接存在網址列，不另外放 state ——
 * 站內通知的連結是 /CodeScan?projectId=…&run=…，人已經停在這頁時點通知，
 * 元件不會重新掛載，若是另存一份 state 就得寫一堆「網址變了要同步」的 effect，
 * 以網址為準則天生一致，重新整理也停在同一個地方。
 * 用 replace 而不是 push：切專案、開關抽屜不該讓上一頁鍵要按好幾次才離開。
 */
export function useScanPage(base) {
  const { toast } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const projId = searchParams.get("projectId") || "";
  const runId = Number(searchParams.get("run")) || null;

  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  /*
   * 「載回來是空的」與「這支 API 根本打不通」必須分開。
   *
   * 兩種情況畫面上都是 0 筆專案，但原因天差地別：前者是「你沒被加進任何專案」，
   * 後者是「後端沒有這個模組」。混在一起講的話，使用者會去找專案經理要權限，
   * 而真正的問題是那支 controller 不存在（實測 /CodeScan/Projects 回 404）。
   */
  const [unavailable, setUnavailable] = useState(null);

  useEffect(() => {
    let alive = true;
    apiGet(`${base}/Projects`)
      .then((d) => {
        if (!alive) return;
        setProjects(Array.isArray(d) ? d : []);
        setUnavailable(null);
        setProjectsLoaded(true);
      })
      .catch((e) => {
        if (!alive) return;
        setProjectsLoaded(true);
        const status = _.get(e, "response.status", 0);
        setUnavailable({ status, base });
        /* 404 是「這個模組還沒上線」，不是操作失敗，不要每次進頁面都跳一則紅字 */
        if (status !== 404) toast(apiErrorMessage(e, "載入專案清單失敗"), "error");
      });
    return () => {
      alive = false;
    };
  }, [base, toast]);

  const patchParams = useCallback(
    (patch) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => {
            if (v === null || v === undefined || v === "") next.delete(k);
            else next.set(k, String(v));
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /* 網址上的 projectId 不在清單裡（專案已刪、沒有範圍）時 project 為 null，由頁面顯示原因 */
  const project = useMemo(() => projects.find((p) => String(p.id) === projId) || null, [projects, projId]);

  const setProjId = useCallback((v) => patchParams({ projectId: v, run: null }), [patchParams]);
  const openRun = useCallback((id) => patchParams({ run: id }), [patchParams]);
  const closeRun = useCallback(() => patchParams({ run: null }), [patchParams]);

  return { projects, projectsLoaded, unavailable, projId, project, setProjId, runId, openRun, closeRun };
}

/** 後端的數字欄位一律轉成 number；null 保持 null（見 fmtCount 的說明） */
const numOrNull = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

/**
 * 檢測／掃描歷史（伺服器分頁，take=20）＋有排隊或執行中的批次時每 5 秒重抓。
 *
 * base 為 "/CodeScan" 或 "/VulnScan"，兩支 Runs 端點回傳同一個外形 { total, has_active, items }。
 * 「最新結果」只取第一頁：清單依排入時間新到舊，最近一次成功的批次一定在最前面幾筆；
 * 翻到後面的頁時保留第一頁算出來的那筆，KPI 才不會跟著翻頁亂跳。
 */
export function useRunHistory(base, projectId, pageSize = 20) {
  const [query, setQuery] = useState({ pid: projectId, page: 1 });
  const [data, setData] = useState({ total: 0, hasActive: false, items: [] });
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  /*
   * 換專案就回第 1 頁並清掉上一個專案的資料。在 render 階段比對而不是用 effect，
   * 否則會先閃一格「新專案的標題配舊專案的歷史」（同 ListPager 的 resetKey 寫法）。
   */
  if (query.pid !== projectId) {
    setQuery({ pid: projectId, page: 1 });
    setData({ total: 0, hasActive: false, items: [] });
    setLatest(null);
    setFailed(false);
  }

  // 請求序號：切專案、翻頁、輪詢交錯時，只有最後發出的那一個能寫入畫面
  const reqIdRef = useRef(0);
  const aliveRef = useRef(true);
  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    []
  );

  const { page } = query;

  const load = useCallback(
    (silent) => {
      if (!projectId) return Promise.resolve();
      reqIdRef.current += 1;
      const id = reqIdRef.current;
      const stale = () => !aliveRef.current || reqIdRef.current !== id;
      const skip = (page - 1) * pageSize;
      if (!silent) {
        setLoading(true);
        setFailed(false);
      }
      return apiGet(`${base}/Runs`, { projectId: Number(projectId), skip, take: pageSize })
        .then((d) => {
          if (stale()) return;
          const items = (Array.isArray(d && d.items) ? d.items : []).map((r) => ({
            ...r,
            id: Number(r.id),
            status: Number(r.status),
            duration_seconds: numOrNull(r.duration_seconds),
            cancel_requested: Boolean(r.cancel_requested),
          }));
          setData({ total: Number((d && d.total) || 0), hasActive: Boolean(d && d.has_active), items });
          if (skip === 0) setLatest(items.find((r) => r.status === 2 || r.status === 5) || null);
          setFailed(false);
          setLoading(false);
          // 翻頁期間資料被刪到這一頁不存在時，退回上一頁，不要停在空白頁
          if (items.length === 0 && page > 1) setQuery((q) => (q.page === page ? { ...q, page: page - 1 } : q));
        })
        .catch(() => {
          if (stale()) return;
          setLoading(false);
          // 輪詢失敗不動畫面：留著上一次的資料，下一輪再試
          if (!silent) setFailed(true);
        });
    },
    [base, projectId, page, pageSize]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  usePolling(() => load(true), Boolean(projectId) && data.hasActive);

  const setPage = useCallback((p) => setQuery((q) => ({ ...q, page: Math.max(1, p) })), []);

  /** 排入新批次後回第 1 頁看它；本來就在第 1 頁時 setQuery 不會觸發 effect，所以自己重抓 */
  const showFirstPage = useCallback(() => {
    if (page === 1) load(true);
    else setPage(1);
  }, [page, load, setPage]);

  return {
    items: data.items,
    total: data.total,
    hasActive: data.hasActive,
    latest,
    page,
    totalPages: Math.max(1, Math.ceil(data.total / pageSize)),
    setPage,
    loading,
    failed,
    reload: () => load(false),
    refresh: () => load(true),
    showFirstPage,
  };
}

/**
 * 單一批次的報告（抽屜用）。批次還在排隊或執行中就每 5 秒重抓，
 * 跑完的那一次回應會帶終態，輪詢自然停下。
 */
export function useRunReport(base, runId) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reqIdRef = useRef(0);

  const load = useCallback(
    (silent) => {
      if (!runId) return Promise.resolve();
      reqIdRef.current += 1;
      const id = reqIdRef.current;
      if (!silent) {
        setLoading(true);
        setError("");
      }
      return apiGet(`${base}/Report/${runId}`)
        .then((d) => {
          if (reqIdRef.current !== id) return;
          setReport(d || null);
          setError("");
          setLoading(false);
        })
        .catch((e) => {
          if (reqIdRef.current !== id) return;
          setLoading(false);
          if (!silent) setError(apiErrorMessage(e, "載入報告失敗"));
        });
    },
    [base, runId]
  );

  useEffect(() => {
    setReport(null);
    load(false);
    // 關抽屜（runId 變 null）或換一筆時，讓還在路上的舊請求作廢
    return () => {
      reqIdRef.current += 1;
    };
  }, [load]);

  const runStatus = report && report.run ? report.run.status : null;
  usePolling(() => load(true), Boolean(runId) && runStatus != null && isActiveStatus(runStatus));

  return { report, loading, error, reload: () => load(false) };
}
